import { Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getToBeApprovedMessages, updateToBeApprovedMessages } from 'API/linkedin-funnel';
import { updateContactIsActive } from 'API/linkedin-funnel-search';
import { ErrorMessages, ProductGroups, ZoomLevels } from 'enums';
import { getCoPilotRouteForPathAndTab } from 'features/co-pilot/co-pilot-routes';
import { FunnelTypes, MessageSender, MessageToApprove } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import useAppSync from 'hooks/use-app-sync';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import ContactDrawer from '../campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import useContactDrawer from '../campaign-builder/use-contact-drawer';
import { useLinkedinAccountsQuery } from '../linkedin-accounts/hooks';
import ApproveContactsDialog from '../campaign-overview/approve-contacts-dialog';
import ApproveMessagesDialog from '../campaign-overview/approve-messages-dialog';
import ConfirmDeclineMessageDialog from '../campaign-overview/confirm-decline-message-dialog';
import AccountAlreadyExistsDialog from '../linkedin-accounts/account-already-exists-dialog';
import AccountAlreadySyncedDialog from '../linkedin-accounts/account-already-synced-dialog';
import AppSyncDialog from '../linkedin-accounts/app-sync-dialog/app-sync-dialog';
import FairUsePauseDialog from '../linkedin-accounts/fair-use-pause-dialog';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';
import OnboardingIncompleteDialog from '../linkedin-accounts/onboarding-incomplete-dialog';
import RecruiterParallelLoginDialog from '../linkedin-accounts/recruiter-parallel-login-dialog';
import TwoFaNotEnabledDialog from '../linkedin-accounts/two-fa-not-enabled-dialog';
import WaitBeforeSyncDialog from '../linkedin-accounts/wait-before-sync-dialog';
import TmHeader from './tm-header';
import TmLinkedinAccounts from './tm-linkedin-accounts';
import TmMessageApproval from './tm-message-approval';
import TmMessageReply from './tm-message-reply';
import useTasks from './use-tasks';
import { Tabs } from './task-manager.types';

interface Props {
  productGroup: ProductGroups;

  handleOpenCopilotLicenseExpiredDialog(): void;
}

const TaskManager: React.FC<Props> = ({ productGroup, handleOpenCopilotLicenseExpiredDialog }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);
  const queryClient = useQueryClient();

  const type =
    productGroup === ProductGroups.HR_BOOSTER
      ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      : productGroup === ProductGroups.LEAD_BOOSTER
      ? FunnelTypes.SOCIAL_SELLING_CO_PILOT
      : undefined;

  const {
    data: linkedinAccounts = [],
    error: linkedinAccountsError,
    isLoading: linkedinAccountsLoading,
  } = useLinkedinAccountsQuery({ prioritizeCurrentUser: true });
  const [selectedLinkedinAccounts, setSelectedLinkedinAccounts] = useState<string[]>([]);
  const [accountsInitiated, setAccountsInitiated] = useState(false);

  const [activeTaskFilter, setActiveTaskFilter] = useState(Tabs.ALL);

  const {
    tasks,
    isLoading: tasksLoading,
    isRefetching: tasksRefetching,
  } = useTasks(type, accountsInitiated, selectedLinkedinAccounts, activeTaskFilter);

  const {
    // app sync dialog props
    appSyncDialog,
    allCountryCodes,
    handleOpenAppSyncAndSynchronizeLiAccount,
    handleOpenAppSyncAndVerifyPin,
    handleOpenAppSyncThirdPartyWebsite,
    handleOpenAppSyncAndSolveCaptcha,
    handleOpenAppSyncAndCorrectAccessData,
    handleCloseAppSyncDialog,
    increaseActiveStepIndex,
    decreaseActiveStepIndex,
    handleAppSyncLinkedinProductChange,
    handleAppSyncCountryChange,
    handleAppSyncCityChange,
    handleAppSyncUserNameChange,
    handleAppSyncPasswordChange,
    handleCreateAndSynchronizeLiAccount,
    handleAppSyncVerificationCodeChange,
    handleVerifyLinkedinAccountFromAppSync,
    handleImageClickFromAppSync,
    handleDeleteMarkerFromAppSync,
    handleSolveCaptchaFromAppSync,
    handleSelectContractFromAppSync,
    handleAddContractFromAppSync,
    handleEditAndSynchronizeLiAccount,
    handleReSynchronizeLiAccount,
    handleThirdPartyClickFromAppSync,
    handleThirdPartyTextChange,
    handleSetThirdPartyTextFromAppSync,
    // other props
    accountAlreadyExistsDialog,
    handleCloseAccountAlreadyExistsDialog,
    waitBeforeSyncDialog,
    handleCloseWaitBeforeSyncDialog,
    accountAlreadySyncedDialog,
    handleCloseAccountAlreadySyncedDialog,
  } = useAppSync(true);

  const [recruiterParallelLoginDialog, setRecruiterParallelLoginDialog] = useState<{
    open: boolean;
    loading: boolean;
    account: LinkedinAccount | null;
  }>({
    open: false,
    loading: false,
    account: null,
  });
  const [onboardingIncompleteDialog, setOnboardingIncompleteDialog] = useState<{
    open: boolean;
    loading: boolean;
    account: LinkedinAccount | null;
  }>({
    open: false,
    loading: false,
    account: null,
  });
  const [fairUsePauseDialog, setFairUsePauseDialog] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const [twoFaNotEnabledDialog, setTwoFaNotEnabledDialog] = useState<{ open: boolean; account: LinkedinAccount | null }>({
    open: false,
    account: null,
  });

  /** Kampagne, deren wartende Kontakte gerade freigegeben werden. */
  const [approveContactsFunnelId, setApproveContactsFunnelId] = useState<string | null>(null);
  const [approveMessagesDialog, setApproveMessagesDialog] = useState<{
    open: boolean;
    loading: boolean;
    funnelId: string | null;
    messages: MessageToApprove[];
    messageIndex: number;
    messageSender: MessageSender | null;
    messageToEditText: string | null;
    messageToEditIndex: number | null;
  }>({
    open: false,
    loading: false,
    funnelId: null,
    messages: [],
    messageIndex: 0,
    messageSender: null,
    messageToEditText: null,
    messageToEditIndex: null,
  });
  const [confirmDeclineMessageDialog, setConfirmDeclineMessageDialog] = useState<{ open: boolean; loading: boolean }>({
    open: false,
    loading: false,
  });
  const [approveMessageSuccess, setApproveMessageSuccess] = useState(false);
  const [allMessagesProcessed, setAllMessagesProcessed] = useState(false);

  const { contactDrawer, handleOpenContactDrawer, handleUpdateDrawerContactIsActive, handleCloseContactDrawer } = useContactDrawer();

  useEffect(() => {
    if (!_.isNil(linkedinAccountsError)) {
      handleOpenErrorDialog(linkedinAccountsError, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
  }, [linkedinAccountsError, handleOpenErrorDialog]);

  // Voreinstellung: alle Konten — einmal, beim ersten Laden. Bisher stand hier nur das erste
  // Konto, und jedes Neuladen der Kontenliste setzte die Auswahl wieder darauf zurück.
  useEffect(() => {
    if (!accountsInitiated && !_.isEmpty(linkedinAccounts)) {
      setSelectedLinkedinAccounts(linkedinAccounts.map((account) => account._id));
      setAccountsInitiated(true);
    }
  }, [linkedinAccounts, accountsInitiated]);

  /**
   * Linkedin account filter handlers
   */

  const handleSelectedLinkedinAccountChange = (id: string) => {
    return async () => {
      const updatedSelectedLinkedinAccounts = [...selectedLinkedinAccounts];

      if (updatedSelectedLinkedinAccounts.includes(id)) {
        _.remove(updatedSelectedLinkedinAccounts, (accountId) => accountId === id);
      } else {
        updatedSelectedLinkedinAccounts.push(id);
      }

      setSelectedLinkedinAccounts(updatedSelectedLinkedinAccounts);
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    };
  };

  const handleSelectAllLinkedinAccounts = async () => {
    const linkedinAccountIds = linkedinAccounts.map((account) => account._id);
    setSelectedLinkedinAccounts(linkedinAccountIds);
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const handleUnselectAllLinkedinAccounts = async () => {
    setSelectedLinkedinAccounts([]);
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  /**
   * Task filter handlers
   */

  const activeTaskFilterChange = (newFilter: Tabs) => {
    return async () => {
      if (newFilter !== activeTaskFilter) {
        setActiveTaskFilter(newFilter);
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
    };
  };

  /**
   * Approve messages dialog handlers
   */

  const handleOpenApproveMessagesDialog = (funnelId: string) => {
    return async () => {
      setApproveMessagesDialog((prev) => ({ ...prev, open: true, loading: true, funnelId }));

      const res = await getToBeApprovedMessages(funnelId);

      if (!res.error) {
        const resMessages: MessageToApprove[] = res.messages || [];
        const messagesWithoutApproved = resMessages.map((msg) => {
          if (!_.isNil(msg.approved)) {
            delete msg.approved;
          }
          return msg;
        });
        setApproveMessagesDialog((prev) => ({ ...prev, loading: false, messages: messagesWithoutApproved, messageSender: res.sender || null }));
      } else {
        setApproveMessagesDialog((prev) => ({ ...prev, open: false, loading: false }));
        handleOpenErrorDialog(res.error, '/messages/campaign/:id/pending', 'get');
      }
    };
  };

  const handleRemoveContact = (linkedInURL: string, idx: number) => {
    return async () => {
      const { messages, funnelId } = approveMessagesDialog;

      if (!_.isNil(funnelId)) {
        const updateMessages = [...messages];

        const messageToApprove = updateMessages[idx];
        messageToApprove.approved = false;

        setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));

        const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

        const [resUpdateMessages, resRemoveContact] = await Promise.all([
          updateToBeApprovedMessages(funnelId, [messageToApprove]),
          updateContactIsActive(funnelId, [linkedInURL], false),
        ]);

        if (!resUpdateMessages.error) {
          setApproveMessagesDialog((prev) => ({
            ...prev,
            loading: false,
            messageIndex: 0,
            messages: prev.messages.filter((message) => _.isNil(message.approved)),
          }));
          if (messagesToApprove.length === 0) {
            handleCloseApproveMessagesDialog();
            handleOpenAllMessagesProcessedSnackbar();
          }
        } else {
          setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
          handleOpenErrorDialog(resUpdateMessages.error, '/messages/bulk-approve', 'post');
        }

        if (resRemoveContact.error) {
          handleOpenErrorDialog(resRemoveContact.error, '/campaigns/:id/contacts', 'put');
        }
      }
    };
  };

  const handleNextMessage = () => {
    const { messages } = approveMessagesDialog;
    setApproveMessagesDialog((prev) => ({
      ...prev,
      messageIndex: prev.messageIndex === messages.length - 1 ? 0 : prev.messageIndex + 1,
      messageToEditText: null,
      messageToEditIndex: null,
    }));
  };

  const handlePrevMessage = () => {
    const { messages } = approveMessagesDialog;
    setApproveMessagesDialog((prev) => ({
      ...prev,
      messageIndex: prev.messageIndex === 0 ? messages.length - 1 : prev.messageIndex - 1,
      messageToEditText: null,
      messageToEditIndex: null,
    }));
  };

  const handleEditMessage = (idx: number) => {
    return () => {
      const message = approveMessagesDialog.messages[idx];
      if (_.isNil(message)) {
        console.warn(`handleEditMessage: Tried to access message at index ${idx}, but it was undefined.`, approveMessagesDialog.messages);
        return;
      }
      setApproveMessagesDialog((prev) => ({ ...prev, messageToEditText: message.text, messageToEditIndex: idx }));
    };
  };

  const handleCancelEditMessage = () => {
    setApproveMessagesDialog((prev) => ({ ...prev, messageToEditText: null, messageToEditIndex: null }));
  };

  const handleMessageToApproveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApproveMessagesDialog((prev) => ({ ...prev, messageToEditText: event.target.value }));
  };

  const handleSaveAndApproveMessage = async () => {
    const { funnelId, messages, messageToEditText, messageToEditIndex } = approveMessagesDialog;

    if (!_.isNil(funnelId) && !_.isNil(messageToEditText) && !_.isNil(messageToEditIndex)) {
      const updateMessages = [...messages];

      const messageToApprove = updateMessages[messageToEditIndex];
      if (_.isNil(messageToApprove)) {
        console.warn('handleSaveAndApproveMessage: messageToApprove is undefined', { messageToEditIndex, updateMessages });
        return;
      }
      messageToApprove.approved = true;
      messageToApprove.text = messageToEditText;

      setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));

      const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

      const res = await updateToBeApprovedMessages(funnelId, [messageToApprove]);

      if (res.error) {
        setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
        if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/messages/bulk-approve', 'post');
        }
      } else {
        setApproveMessagesDialog((prev) => ({
          ...prev,
          loading: false,
          messageIndex: 0,
          messages: prev.messages.filter((message) => _.isNil(message.approved)),
        }));
        if (messagesToApprove.length === 0) {
          handleCloseApproveMessagesDialog();
          handleOpenAllMessagesProcessedSnackbar();
        } else {
          handleOpenApproveMessageSuccessSnackbar();
        }
      }
    }
  };

  const handleApproveMessage = (idx: number) => {
    return async () => {
      const { messages, funnelId } = approveMessagesDialog;

      if (!_.isNil(funnelId)) {
        const updateMessages = [...messages];

        const messageToApprove = updateMessages[idx];
        if (_.isNil(messageToApprove)) {
          console.warn('handleApproveMessage: messageToApprove is undefined', { idx, updateMessages });
          return;
        }
        messageToApprove.approved = true;

        setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));

        const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

        const res = await updateToBeApprovedMessages(funnelId, [messageToApprove]);

        if (res.error) {
          setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
          if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
            handleOpenCopilotLicenseExpiredDialog();
          } else {
            handleOpenErrorDialog(res.error, '/messages/bulk-approve', 'post');
          }
        } else {
          setApproveMessagesDialog((prev) => ({
            ...prev,
            loading: false,
            messageIndex: 0,
            messages: prev.messages.filter((message) => _.isNil(message.approved)),
          }));
          if (messagesToApprove.length === 0) {
            handleCloseApproveMessagesDialog();
            handleOpenAllMessagesProcessedSnackbar();
          } else {
            handleOpenApproveMessageSuccessSnackbar();
          }
        }
      }
    };
  };

  const handleContactApprovalRemainingChange = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
  };

  const handleCloseApproveMessagesDialog = async () => {
    setApproveMessagesDialog({
      open: false,
      loading: false,
      funnelId: null,
      messages: [],
      messageIndex: 0,
      messageSender: null,
      messageToEditText: null,
      messageToEditIndex: null,
    });

    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
  };

  /**
   * Confirm message cancellation dialog handlers
   */

  const handleOpenConfirmDeclineMessageDialog = () => {
    setConfirmDeclineMessageDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseConfirmDeclineMessageDialog = () => {
    setConfirmDeclineMessageDialog({ open: false, loading: false });
  };

  const handleDenyMessage = (deactivateContact: boolean = false) => {
    return async () => {
      const { messages, funnelId, messageIndex } = approveMessagesDialog;

      if (!_.isNil(funnelId)) {
        const updateMessages = [...messages];

        const messageToApprove = updateMessages[messageIndex];
        messageToApprove.approved = false;

        setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));
        setConfirmDeclineMessageDialog((prev) => ({ ...prev, loading: true }));

        const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

        const res = await updateToBeApprovedMessages(funnelId, [messageToApprove]);

        if (res.error) {
          if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
            handleOpenCopilotLicenseExpiredDialog();
          } else {
            handleOpenErrorDialog(res.error, '/messages/bulk-approve', 'post');
          }
          setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
          setConfirmDeclineMessageDialog((prev) => ({ ...prev, loading: false }));
        } else {
          if (deactivateContact) {
            const url = messageToApprove.linkedInURL;

            if (url) {
              const resDeactivate = await updateContactIsActive(funnelId, [url], false);

              if (resDeactivate.error) {
                handleOpenErrorDialog(resDeactivate?.error, '/campaigns/:id/contacts', 'put');
              }
            }
          }
          setApproveMessagesDialog((prev) => ({
            ...prev,
            loading: false,
            messageIndex: 0,
            messages: prev.messages.filter((message) => _.isNil(message.approved)),
          }));
          if (messagesToApprove.length === 0) {
            handleCloseApproveMessagesDialog();
            handleOpenAllMessagesProcessedSnackbar();
          }

          handleCloseConfirmDeclineMessageDialog();
        }
      }
    };
  };

  /**
   * Success snackbar handlers
   */

  const handleOpenApproveMessageSuccessSnackbar = () => {
    setApproveMessageSuccess(true);
  };

  const handleCloseApproveMessageSuccessSnackbar = () => {
    setApproveMessageSuccess(false);
  };

  const handleOpenAllMessagesProcessedSnackbar = () => {
    setAllMessagesProcessed(true);
  };

  const handleCloseAllMessagesProcessedSnackbar = () => {
    setAllMessagesProcessed(false);
  };

  /**
   * Message reply handlers
   */

  const handleMessageReplyTaskClick = (funnelId: string, liAccountId: string | undefined) => {
    return () => {
      if (!liAccountId) return;

      const settings = { funnelId, liAccountId };
      localStorage.setItem('inbox-settings', JSON.stringify(settings));
      const route = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.INBOX);
      if (route) {
        navigate(route);
      } else {
        setActiveTab(NavigationStates.INBOX, false);
      }
    };
  };

  /**
   * Recruiter parallel login dialog handlers
   */

  const handleOpenRecruiterParallelLoginDialog = (account: LinkedinAccount) => {
    return () => {
      setRecruiterParallelLoginDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseRecruiterParallelLoginDialog = () => {
    setRecruiterParallelLoginDialog({ open: false, loading: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromParallelLogin = () => {
    const { account } = recruiterParallelLoginDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseRecruiterParallelLoginDialog();
    }
  };

  /**
   * Onboarding incomplete dialog handlers
   */

  const handleOpenOnboardingIncompleteDialog = (account: LinkedinAccount) => {
    return () => {
      setOnboardingIncompleteDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseOnboardingIncompleteDialog = () => {
    setOnboardingIncompleteDialog({ open: false, loading: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromOnboardingIncomplete = () => {
    const { account } = onboardingIncompleteDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseOnboardingIncompleteDialog();
    }
  };

  /**
   * Fair use pause dialog handlers
   */

  const handleOpenFairUsePauseDialog = () => {
    setFairUsePauseDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseFairUsePauseDialog = () => {
    setFairUsePauseDialog({ open: false });
  };

  /**
   * 2FA not enabled dialog
   */

  const handleOpenTwoFaNotEnabledDialog = (account: LinkedinAccount) => {
    return () => {
      setTwoFaNotEnabledDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseTwoFaNotEnabledDialog = () => {
    setTwoFaNotEnabledDialog({ open: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromTwoFaNotEnabled = () => {
    const { account } = twoFaNotEnabledDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseTwoFaNotEnabledDialog();
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <TmHeader
          linkedinAccounts={linkedinAccounts}
          activeLinkedinAccounts={selectedLinkedinAccounts}
          activeTaskFilter={activeTaskFilter}
          tasksRefetching={tasksRefetching}
          taskCounts={tasks}
          handleSelectAllLinkedinAccounts={handleSelectAllLinkedinAccounts}
          handleUnselectAllLinkedinAccounts={handleUnselectAllLinkedinAccounts}
          handleSelectedLinkedinAccountChange={handleSelectedLinkedinAccountChange}
          activeTaskFilterChange={activeTaskFilterChange}
        />
      </div>
      <Stack className={classes.content} gap={'20px'}>
        {tasksLoading || linkedinAccountsLoading ? (
          <PaiDotLoader />
        ) : (
          <>
            {!_.isNil(tasks.fixAccounts) && !_.isEmpty(tasks.fixAccounts) ? (
              <TmLinkedinAccounts
                tasks={tasks.fixAccounts}
                total={tasks.fixAccountsCount}
                syncLoading={appSyncDialog.loading.sync}
                handleOpenAppSyncAndSynchronizeLiAccount={handleOpenAppSyncAndSynchronizeLiAccount}
                handleOpenAppSyncAndCorrectAccessData={handleOpenAppSyncAndCorrectAccessData}
                handleOpenRecruiterParallelLoginDialog={handleOpenRecruiterParallelLoginDialog}
                handleOpenAppSyncAndVerifyPin={handleOpenAppSyncAndVerifyPin}
                handleOpenAppSyncAndSolveCaptcha={handleOpenAppSyncAndSolveCaptcha}
                handleOpenAppSyncThirdPartyWebsite={handleOpenAppSyncThirdPartyWebsite}
                handleOpenOnboardingIncompleteDialog={handleOpenOnboardingIncompleteDialog}
                handleOpenFairUsePauseDialog={handleOpenFairUsePauseDialog}
                handleOpenTwoFaNotEnabledDialog={handleOpenTwoFaNotEnabledDialog}
              />
            ) : null}
            {!_.isNil(tasks.approveContacts) && !_.isEmpty(tasks.approveContacts) ? (
              <TmMessageApproval
                variant={'contacts'}
                tasks={tasks.approveContacts}
                total={tasks.approveContactsCount}
                handleOpenApproveMessagesDialog={(funnelId: string) => () => setApproveContactsFunnelId(funnelId)}
              />
            ) : null}
            {!_.isNil(tasks.approveMessages) && !_.isEmpty(tasks.approveMessages) ? (
              <TmMessageApproval
                tasks={tasks.approveMessages}
                total={tasks.approveMessagesCount}
                handleOpenApproveMessagesDialog={handleOpenApproveMessagesDialog}
              />
            ) : null}
            {!_.isNil(tasks.replyMessages) && !_.isEmpty(tasks.replyMessages) ? (
              <TmMessageReply
                tasks={tasks.replyMessages}
                total={tasks.replyMessagesCount}
                handleMessageReplyTaskClick={handleMessageReplyTaskClick}
              />
            ) : null}
          </>
        )}
      </Stack>

      <AppSyncDialog
        open={appSyncDialog.open}
        product={productGroup}
        handleClose={handleCloseAppSyncDialog}
        isStarterExperience={appSyncDialog.isStarterExperience}
        activeStepIndex={appSyncDialog.activeStepIndex}
        loading={appSyncDialog.loading}
        syncProcessingStatus={appSyncDialog.syncProcessingStatus}
        syncError={appSyncDialog.error}
        selectedLinkedinProduct={appSyncDialog.selectedLinkedinProduct}
        allCountryCodes={allCountryCodes}
        selectedCountry={appSyncDialog.selectedCountry}
        selectedCity={appSyncDialog.selectedCity}
        handleLinkedinProductChange={handleAppSyncLinkedinProductChange}
        handleCountryChange={handleAppSyncCountryChange}
        handleCityChange={handleAppSyncCityChange}
        userName={appSyncDialog.userName}
        password={appSyncDialog.password}
        linkedinAccount={appSyncDialog.linkedinAccount}
        verificationCode={appSyncDialog.verificationCode}
        captcha={appSyncDialog.captcha}
        markerPosition={appSyncDialog.markerPosition}
        thirdPartyScreenshot={appSyncDialog.thirdPartyScreenshot}
        thirdPartyText={appSyncDialog.thirdPartyText}
        linkedinContracts={appSyncDialog.foundContractNames}
        selectedContract={appSyncDialog.selectedContract}
        increaseActiveStepIndex={increaseActiveStepIndex}
        decreaseActiveStepIndex={decreaseActiveStepIndex}
        handleUserNameChange={handleAppSyncUserNameChange}
        handlePasswordChange={handleAppSyncPasswordChange}
        handleCreateAndSynchronizeLiAccount={handleCreateAndSynchronizeLiAccount}
        handleVerificationCodeChange={handleAppSyncVerificationCodeChange}
        handleVerifyLinkedinAccountFromAppSync={handleVerifyLinkedinAccountFromAppSync}
        handleImageClick={handleImageClickFromAppSync}
        handleDeleteMarker={handleDeleteMarkerFromAppSync}
        handleSolveCaptcha={handleSolveCaptchaFromAppSync}
        handleThirdPartyClick={handleThirdPartyClickFromAppSync}
        handleThirdPartyTextChange={handleThirdPartyTextChange}
        handleSetThirdPartyTextFromAppSync={handleSetThirdPartyTextFromAppSync}
        handleSelectContract={handleSelectContractFromAppSync}
        handleAddContractFromAppSync={handleAddContractFromAppSync}
        handleEditAndSynchronizeLiAccount={handleEditAndSynchronizeLiAccount}
        handleReSynchronizeLiAccount={handleReSynchronizeLiAccount}
        selectedLicenseId={null}
        handleLicenseSelect={() => {}}
      />
      <AccountAlreadyExistsDialog
        open={accountAlreadyExistsDialog.open}
        userEmail={accountAlreadyExistsDialog.userEmail}
        handleClose={handleCloseAccountAlreadyExistsDialog}
      />
      <WaitBeforeSyncDialog
        open={waitBeforeSyncDialog.open}
        waitingTime={waitBeforeSyncDialog.waitingTime}
        handleClose={handleCloseWaitBeforeSyncDialog}
      />
      <AccountAlreadySyncedDialog open={accountAlreadySyncedDialog.open} handleClose={handleCloseAccountAlreadySyncedDialog} />
      <FairUsePauseDialog open={fairUsePauseDialog.open} handleClose={handleCloseFairUsePauseDialog} />
      <TwoFaNotEnabledDialog
        open={twoFaNotEnabledDialog.open}
        account={twoFaNotEnabledDialog.account}
        handleClose={handleCloseTwoFaNotEnabledDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromTwoFaNotEnabled}
      />
      <RecruiterParallelLoginDialog
        open={recruiterParallelLoginDialog.open}
        loading={recruiterParallelLoginDialog.loading}
        handleClose={handleCloseRecruiterParallelLoginDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromParallelLogin}
      />
      <OnboardingIncompleteDialog
        open={onboardingIncompleteDialog.open}
        loading={onboardingIncompleteDialog.loading}
        handleClose={handleCloseOnboardingIncompleteDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromOnboardingIncomplete}
      />
      <ApproveContactsDialog
        open={!_.isNil(approveContactsFunnelId)}
        funnelId={approveContactsFunnelId}
        handleClose={() => setApproveContactsFunnelId(null)}
        handleRemainingChange={handleContactApprovalRemainingChange}
        handleOpenContactDrawer={handleOpenContactDrawer}
      />
      <ApproveMessagesDialog
        open={approveMessagesDialog.open}
        loading={approveMessagesDialog.loading}
        messages={approveMessagesDialog.messages}
        messageIndex={approveMessagesDialog.messageIndex}
        messageSender={approveMessagesDialog.messageSender}
        messageToEditText={approveMessagesDialog.messageToEditText}
        messageToEditIndex={approveMessagesDialog.messageToEditIndex}
        handleNextMessage={handleNextMessage}
        handlePrevMessage={handlePrevMessage}
        handleRemoveContact={handleRemoveContact}
        handleEditMessage={handleEditMessage}
        handleCancelEditMessage={handleCancelEditMessage}
        handleMessageToApproveChange={handleMessageToApproveChange}
        handleSaveAndApproveMessage={handleSaveAndApproveMessage}
        handleApproveMessage={handleApproveMessage}
        handleOpenConfirmDeclineMessageDialog={handleOpenConfirmDeclineMessageDialog}
        handleClose={handleCloseApproveMessagesDialog}
        handleOpenContactDrawer={handleOpenContactDrawer}
      />
      <ConfirmDeclineMessageDialog
        open={confirmDeclineMessageDialog.open}
        loading={confirmDeclineMessageDialog.loading}
        handleClose={handleCloseConfirmDeclineMessageDialog}
        handleDenyMessage={handleDenyMessage()}
        handleDenyMessageAndDeactivateContact={handleDenyMessage(true)}
      />
      <PaiSuccessAlert
        open={approveMessageSuccess}
        message={t('approveMessageSuccessMessage')}
        position={'bottom'}
        handleClose={handleCloseApproveMessageSuccessSnackbar}
      />
      <PaiSuccessAlert
        open={allMessagesProcessed}
        message={t('allMessagesProcessed')}
        position={'bottom'}
        handleClose={handleCloseAllMessagesProcessedSnackbar}
      />
      <ContactDrawer
        open={contactDrawer.open}
        productGroup={productGroup}
        funnelId={contactDrawer.funnelId}
        contactIndex={contactDrawer.contactIndex}
        profileId={contactDrawer.profileId}
        aiJudgement={contactDrawer.aiJudgement}
        aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
        assessment={contactDrawer.assessment}
        percentiles={contactDrawer.percentiles}
        isActive={contactDrawer.isActive}
        contactDetails={contactDrawer.contactDetails}
        aboutContact={contactDrawer.aboutContact}
        aboutCompany={contactDrawer.aboutCompany}
        handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
        handleCloseDrawer={handleCloseContactDrawer}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  header: {
    padding: '28px 40px 20px 40px',
  },
  content: {
    height: '100%',
    padding: '0 40px 40px 40px',
    overflow: 'auto',
  },
}));

export default TaskManager;
